import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../api/api';

export default function OtpVerify() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [tempUser, setTempUser] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const autoSentRef = useRef(false);
  const navigate = useNavigate();

  const startOtpCooldown = useCallback(() => {
    setOtpSent(true);
    setOtpCooldown(30);
  }, []);

  const generateAndSendOtp = useCallback(async (Vcode, UserType, Vname, Vid) => {
    if (sendingOtp || otpCooldown > 0) return;  // 🔒 Prevent double call
    
    try {
      setSendingOtp(true);
      setError('');
      // Call GenerateOtp API
      const generateRes = await api.GenerateOtp(Vcode, UserType, Vid);
      const otpData = generateRes?.GenerateOtpjs?.Table?.[0];
      
      if (otpData?.GeneratedOTP) {
        const generatedOTP = otpData.GeneratedOTP.toString();
        
        // Get OTP source from localStorage to determine template
        const otpSource = localStorage.getItem('otpSource');
        const templateName = otpSource === 'register' ? 'RegisterOtp' : 'ResetOtp';
        
        // Call SendOtp API
        await api.SendOtp({
          Name: Vname,
          Otp: generatedOTP,
          MobileNumber: Vcode,
          TemplateName: templateName
        });

        startOtpCooldown();
        setMessage('OTP sent successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('Failed to generate OTP');
      }
    } catch (err) {
      console.error('Error generating/sending OTP:', err);
      setError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  }, [otpCooldown, sendingOtp, startOtpCooldown]);

useEffect(() => {
  const user = JSON.parse(localStorage.getItem('tempUser'));

  if (user) {
    setTempUser(user);

    // 🔥 AUTO CALL OTP API AFTER NAVIGATION
    if (!autoSentRef.current) {
      autoSentRef.current = true;
      generateAndSendOtp(user.Vcode, user.UserType, user.Vname, user.Vid);
    }

  } else {
    navigate('/');
  }
}, [generateAndSendOtp, navigate]);

useEffect(() => {
  if (otpCooldown <= 0) return undefined;

  const timer = setInterval(() => {
    setOtpCooldown((seconds) => Math.max(seconds - 1, 0));
  }, 1000);

  return () => clearInterval(timer);
}, [otpCooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp) {
      return setError('Please enter OTP.');
    }

    try {
      setVerifying(true);
      const verifyRes = await api.VerifyOtp(
        tempUser.Vcode,
        tempUser.UserType,
        otp,
        tempUser.Vid
      );
      const verifyData = verifyRes?.VerifyOtpjs?.Table?.[0];
      
      if (verifyData?.Status === 1) {
        localStorage.setItem('tempUser', JSON.stringify(tempUser));
        navigate('/newpassword');
      } else {
        setError('Invalid OTP.');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (!tempUser) return null;

  return (
    <div className="flex flex-col justify-center items-center p-4 min-h-[80vh] relative w-full max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/password')}
        className="absolute flex items-center gap-2 font-semibold text-gray-600 transition top-4 left-4 hover:text-red-700"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="w-full max-w-sm p-8 mt-10 bg-white shadow-lg rounded-2xl animate-fade-in">
           <div className="flex items-center justify-center p-4">
  <img
    src="/assets/AnnamLogo.PNG"
    alt="Logo"
    className="w-auto h-16 mx-auto"
  />
</div>
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-800">Verify OTP</h1>
          <p className="mt-2 text-gray-600">Enter the OTP sent to your mobile.</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <input
            value={tempUser.Vcode}
            readOnly
            placeholder="Mobile Number"
            className="w-full p-3 text-center bg-gray-100 border rounded-lg"
          />
          <button
            type="button"
            disabled={sendingOtp || otpCooldown > 0}
            onClick={() => generateAndSendOtp(
              tempUser.Vcode,
              tempUser.UserType,
              tempUser.Vname,
              tempUser.Vid
            )}
            className="text-sm text-center text-gray-600 disabled:opacity-50"
          >
            {sendingOtp ? 'Sending...' : otpCooldown > 0 ? `Send OTP (${otpCooldown}s)` : otpSent ? 'Resend OTP' : 'Send OTP'}
          </button>
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            className="w-full p-3 text-center border rounded-lg"
          />
          {message && <p className="text-sm text-center text-green-500">{message}</p>}
          {error && <p className="text-sm text-center text-red-500">{error}</p>}
          <button type="submit" disabled={verifying} className="w-full py-3 font-bold text-white transition bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50">
            {verifying ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>
      </div>
    </div>
  );
}
